/**
 * 物流管理模块 - API 工厂函数
 * 
 * @module logistics
 * @version 1.0.0
 * @since 2026-01-23
 */

import { API_ROUTES } from '../routes'
import type { PaginationResult } from '../../types/api'
import type {
  LogisticsProvider,
  LogisticsOrder,
  LogisticsTrack,
  LogisticsReview,
  LogisticsSettlement,
  LogisticsSettlementItem,
  LogisticsProviderOption,
  LogisticsProviderStatistics,
  LogisticsOrderStatistics,
  SettlementImportResult,
  SettlementImportTemplate,
  CreateLogisticsProviderForm,
  UpdateLogisticsProviderForm,
  CreateLogisticsOrderForm,
  UpdateLogisticsOrderForm,
  CreateLogisticsOrderFromShipmentForm,
  CreateLogisticsTrackForm,
  CreateLogisticsReviewForm,
  CreateLogisticsSettlementForm,
  UpdateLogisticsSettlementForm,
  ImportLogisticsSettlementForm,
  UpdateSettlementItemForm,
  UpdateLogisticsOrderStatusForm,
  LogisticsProviderQueryParams,
  LogisticsOrderQueryParams,
  LogisticsSettlementQueryParams,
  SettlementItemQueryParams,
  LogisticsStatisticsQueryParams,
  LogisticsProviderOptionsQueryParams,
} from '../../types/logistics'

/**
 * HTTP 请求适配器接口
 */
export interface HttpAdapter {
  get<T = any>(url: string, config?: any): Promise<T>
  post<T = any>(url: string, data?: any, config?: any): Promise<T>
  put<T = any>(url: string, data?: any, config?: any): Promise<T>
  patch<T = any>(url: string, data?: any, config?: any): Promise<T>
  delete<T = any>(url: string, config?: any): Promise<T>
}

/**
 * 物流供应商 API 路径常量
 */
export const LOGISTICS_PROVIDER_API = {
  LIST: API_ROUTES.logistic.list,
  OPTIONS: API_ROUTES.logistic.providersOptions,
  GENERATE_CODE: API_ROUTES.logistic.providersGenerateCode,
  CREATE: API_ROUTES.logistic.providers,
  DETAIL: (id: number) => API_ROUTES.logistic.detail(id),
  UPDATE: (id: number) => API_ROUTES.logistic.logisticsProviders(id),
  DELETE: (id: number) => API_ROUTES.logistic.delete(id),
  TOGGLE_STATUS: (id: number) => API_ROUTES.logistic.update(id),
  STATISTICS: (id: number) => API_ROUTES.logistic.statistics(id),
} as const

/**
 * 物流订单 API 路径常量
 */
export const LOGISTICS_ORDER_API = {
  LIST: API_ROUTES.logistic.orders,
  STATISTICS: API_ROUTES.logistic.ordersStatistics,
  CREATE: API_ROUTES.logistic.orders,
  FROM_SHIPMENT: API_ROUTES.logistic.fromShipment,
  DETAIL: (id: number) => API_ROUTES.logistic.logisticsOrders(id),
  UPDATE: (id: number) => API_ROUTES.logistic.logisticsOrders(id),
  DELETE: (id: number) => API_ROUTES.logistic.logisticsOrders(id),
  UPDATE_STATUS: (id: number) => API_ROUTES.logistic.ordersStatus(id),
  TRACKS: (id: number) => API_ROUTES.logistic.ordersTracks(id),
  ADD_TRACK: (id: number) => API_ROUTES.logistic.tracks(id),
  REVIEW: (id: number) => API_ROUTES.logistic.review(id),
} as const

/**
 * 物流结算 API 路径常量
 */
export const LOGISTICS_SETTLEMENT_API = {
  LIST: API_ROUTES.logistic.settlements,
  TEMPLATE: API_ROUTES.logistic.settlementsTemplate,
  CREATE: API_ROUTES.logistic.settlements,
  DETAIL: (id: number) => API_ROUTES.logistic.logisticsSettlements(id),
  UPDATE: (id: number) => API_ROUTES.logistic.logisticsSettlements(id),
  DELETE: (id: number) => API_ROUTES.logistic.logisticsSettlements(id),
  IMPORT: (id: number) => API_ROUTES.logistic.import(id),
  CONFIRM: (id: number) => API_ROUTES.logistic.confirm(id),
  PAY: (id: number) => API_ROUTES.logistic.pay(id),
  ITEMS: (id: number) => API_ROUTES.logistic.settlementsItems(id),
  UPDATE_ITEM: (id: number, itemId: number) => API_ROUTES.logistic.items(id, itemId),
} as const

/**
 * 创建物流供应商 API
 * 
 * @param client - 请求客户端
 * @returns 物流供应商 API 方法集合
 * 
 * ⚠️ 注意：类型声明假设 client 已自动解包 ApiResponse.data 字段
 * Web端 requestClient 配置了 responseReturn: 'data'，会自动解包
 */
export function createLogisticsProviderApi(client: HttpAdapter) {
  return {
    /**
     * 获取物流供应商列表
     */
    getList: (params?: LogisticsProviderQueryParams) =>
      client.get<PaginationResult<LogisticsProvider>>(LOGISTICS_PROVIDER_API.LIST, { params }),

    /**
     * 获取物流供应商选项列表（下拉选择用）
     * 返回已解包的数据数组
     */
    getOptions: (params?: LogisticsProviderOptionsQueryParams) =>
      client.get<LogisticsProviderOption[]>(LOGISTICS_PROVIDER_API.OPTIONS, { params }),

    /**
     * 生成物流供应商编码
     */
    generateCode: () =>
      client.get<{ code: string }>(LOGISTICS_PROVIDER_API.GENERATE_CODE),

    /**
     * 创建物流供应商
     */
    create: (data: CreateLogisticsProviderForm) =>
      client.post<LogisticsProvider>(LOGISTICS_PROVIDER_API.CREATE, data),

    /**
     * 获取物流供应商详情
     */
    getDetail: (id: number) =>
      client.get<LogisticsProvider>(LOGISTICS_PROVIDER_API.DETAIL(id)),

    /**
     * 更新物流供应商
     */
    update: (id: number, data: UpdateLogisticsProviderForm) =>
      client.put<LogisticsProvider>(LOGISTICS_PROVIDER_API.UPDATE(id), data),

    /**
     * 删除物流供应商
     */
    delete: (id: number) =>
      client.delete<null>(LOGISTICS_PROVIDER_API.DELETE(id)),

    /**
     * 切换物流供应商状态
     */
    toggleStatus: (id: number) =>
      client.patch<LogisticsProvider>(LOGISTICS_PROVIDER_API.TOGGLE_STATUS(id)),

    /**
     * 获取物流供应商统计数据
     */
    getStatistics: (id: number) =>
      client.get<LogisticsProviderStatistics>(LOGISTICS_PROVIDER_API.STATISTICS(id)),
  }
}

/**
 * 创建物流订单 API
 * 
 * @param client - 请求客户端
 * @returns 物流订单 API 方法集合
 * 
 * ⚠️ 注意：类型声明假设 client 已自动解包 ApiResponse.data 字段
 */
export function createLogisticsOrderApi(client: HttpAdapter) {
  return {
    /**
     * 获取物流订单列表
     */
    getList: (params?: LogisticsOrderQueryParams) =>
      client.get<PaginationResult<LogisticsOrder>>(LOGISTICS_ORDER_API.LIST, { params }),

    /**
     * 获取物流订单统计
     */
    getStatistics: (params?: LogisticsStatisticsQueryParams) =>
      client.get<LogisticsOrderStatistics>(LOGISTICS_ORDER_API.STATISTICS, { params }),

    /**
     * 创建物流订单
     */
    create: (data: CreateLogisticsOrderForm) =>
      client.post<LogisticsOrder>(LOGISTICS_ORDER_API.CREATE, data),

    /**
     * 从发货单创建物流订单
     */
    createFromShipment: (data: CreateLogisticsOrderFromShipmentForm) =>
      client.post<LogisticsOrder>(LOGISTICS_ORDER_API.FROM_SHIPMENT, data),

    /**
     * 获取物流订单详情
     */
    getDetail: (id: number) =>
      client.get<LogisticsOrder>(LOGISTICS_ORDER_API.DETAIL(id)),

    /**
     * 更新物流订单
     */
    update: (id: number, data: UpdateLogisticsOrderForm) =>
      client.put<LogisticsOrder>(LOGISTICS_ORDER_API.UPDATE(id), data),

    /**
     * 删除物流订单
     */
    delete: (id: number) =>
      client.delete<null>(LOGISTICS_ORDER_API.DELETE(id)),

    /**
     * 更新物流订单状态
     */
    updateStatus: (id: number, data: UpdateLogisticsOrderStatusForm) =>
      client.patch<LogisticsOrder>(LOGISTICS_ORDER_API.UPDATE_STATUS(id), data),

    /**
     * 获取物流轨迹
     */
    getTracks: (id: number) =>
      client.get<LogisticsTrack[]>(LOGISTICS_ORDER_API.TRACKS(id)),

    /**
     * 添加物流轨迹
     */
    addTrack: (id: number, data: CreateLogisticsTrackForm) =>
      client.post<LogisticsTrack>(LOGISTICS_ORDER_API.ADD_TRACK(id), data),

    /**
     * 提交物流评价
     */
    submitReview: (id: number, data: CreateLogisticsReviewForm) =>
      client.post<LogisticsReview>(LOGISTICS_ORDER_API.REVIEW(id), data),
  }
}

/**
 * 创建物流结算 API
 * 
 * @param client - 请求客户端
 * @returns 物流结算 API 方法集合
 * 
 * ⚠️ 注意：类型声明假设 client 已自动解包 ApiResponse.data 字段
 */
export function createLogisticsSettlementApi(client: HttpAdapter) {
  return {
    /**
     * 获取结算单列表
     */
    getList: (params?: LogisticsSettlementQueryParams) =>
      client.get<PaginationResult<LogisticsSettlement>>(LOGISTICS_SETTLEMENT_API.LIST, { params }),

    /**
     * 获取导入模板
     */
    getTemplate: () =>
      client.get<SettlementImportTemplate>(LOGISTICS_SETTLEMENT_API.TEMPLATE),

    /**
     * 创建结算单
     */
    create: (data: CreateLogisticsSettlementForm) =>
      client.post<LogisticsSettlement>(LOGISTICS_SETTLEMENT_API.CREATE, data),

    /**
     * 获取结算单详情
     */
    getDetail: (id: number) =>
      client.get<LogisticsSettlement>(LOGISTICS_SETTLEMENT_API.DETAIL(id)),

    /**
     * 更新结算单
     */
    update: (id: number, data: UpdateLogisticsSettlementForm) =>
      client.put<LogisticsSettlement>(LOGISTICS_SETTLEMENT_API.UPDATE(id), data),

    /**
     * 删除结算单
     */
    delete: (id: number) =>
      client.delete<null>(LOGISTICS_SETTLEMENT_API.DELETE(id)),

    /**
     * 导入账单数据
     */
    importBill: (id: number, data: ImportLogisticsSettlementForm) =>
      client.post<SettlementImportResult>(LOGISTICS_SETTLEMENT_API.IMPORT(id), data),

    /**
     * 确认结算
     */
    confirm: (id: number) =>
      client.post<LogisticsSettlement>(LOGISTICS_SETTLEMENT_API.CONFIRM(id)),

    /**
     * 标记付款
     */
    markPaid: (id: number) =>
      client.post<LogisticsSettlement>(LOGISTICS_SETTLEMENT_API.PAY(id)),

    /**
     * 获取结算明细列表
     */
    getItems: (id: number, params?: SettlementItemQueryParams) =>
      client.get<PaginationResult<LogisticsSettlementItem>>(LOGISTICS_SETTLEMENT_API.ITEMS(id), { params }),

    /**
     * 更新结算明细
     */
    updateItem: (id: number, itemId: number, data: UpdateSettlementItemForm) =>
      client.put<LogisticsSettlementItem>(LOGISTICS_SETTLEMENT_API.UPDATE_ITEM(id, itemId), data),
  }
}

export type LogisticsProviderApi = ReturnType<typeof createLogisticsProviderApi>
export type LogisticsOrderApi = ReturnType<typeof createLogisticsOrderApi>
export type LogisticsSettlementApi = ReturnType<typeof createLogisticsSettlementApi>

